import React, { useEffect, useMemo, useState } from 'react';
import {
    FaTimes, FaSpinner, FaExclamationTriangle, FaCheckCircle, FaCreditCard,
    FaCalendarAlt, FaGift, FaInfoCircle, FaReceipt, FaChartBar, FaUserClock,
    FaTimesCircle, FaCalendarCheck,
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import CsvExportButton from './customers/CsvExportButton';
import { resolveEffectivePricing } from '../utils/effectivePricing';

// Admin per-customer Billing Summary modal. Combines the customer-facing
// view (outstanding / next charge / promo / spending chart) with admin-only
// insights (LTV / failed payment timeline / 3-month forecast / churn risk).
// Reuses the getCustomerInsights backend action shipped in Direction B.

const fmtAud = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(Number(n) || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtMonthShort = (key) => {
    if (!key) return '';
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-AU', { month: 'short' });
};
const fmtMonth = (key) => {
    if (!key) return '';
    const [y, m] = key.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
};
// Compact $ for above-bar labels — keeps 12 narrow columns readable.
const fmtCompact = (n) => {
    const v = Number(n) || 0;
    if (v === 0) return null;
    if (v >= 10000) return `$${Math.round(v / 1000)}k`;
    if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
    return `$${Math.round(v)}`;
};

const isFailed = (inv) => /^fail/i.test(inv?.status || '');
const isPaid = (inv) => /^paid$/i.test(inv?.status || '');
const isUnpaid = (inv) => !isPaid(inv) && !/^(cancelled|void|refunded)$/i.test(inv?.status || '');

const RISK_STYLES = {
    low: { bg: 'bg-[#3c3836]', border: 'border-[#458588]', text: 'text-[#b8bb26]', label: 'Low' },
    medium: { bg: 'bg-[#3c3320]', border: 'border-[#d79921]', text: 'text-[#fabd2f]', label: 'Medium' },
    high: { bg: 'bg-[#3c1f1f]', border: 'border-[#cc241d]', text: 'text-[#fb4934]', label: 'High' },
};

const AdminBillingSummary = ({ customer, onClose }) => {
    const [invoices, setInvoices] = useState([]);
    const [insights, setInsights] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!customer?.email) return;
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer?.UID, customer?.email]);

    const loadAll = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('adminToken');
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            };
            // Three calls in parallel: invoices, payment methods (for next-charge
            // PM label), and the admin-only insights from Direction B.
            const [invRes, pmRes, insightsRes] = await Promise.all([
                fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        action: 'getBillingHistory',
                        customerEmail: customer.email,
                        email: customer.email,
                        limit: 100,
                    }),
                }),
                fetch(API_ENDPOINTS.ADMIN_BILLING_MANAGEMENT, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        action: 'getPaymentMethods',
                        customerEmail: customer.email,
                        email: customer.email,
                    }),
                }),
                fetch(API_ENDPOINTS.ADMIN_CUSTOMER_MANAGEMENT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
                    body: JSON.stringify({
                        action: 'getCustomerInsights',
                        customerId: customer.UID,
                        customerEmail: customer.email,
                    }),
                }),
            ]);
            const [invData, pmData, insData] = await Promise.all([invRes.json(), pmRes.json(), insightsRes.json()]);
            if (invData?.success) setInvoices(invData.billingHistory || []);
            if (pmData?.success) setPaymentMethods(pmData.paymentMethods || []);
            if (insData?.success) setInsights(insData.insights || null);
            if (!invData?.success && !insData?.success) {
                setError(invData?.error || insData?.error || 'Failed to load billing summary');
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const now = new Date();

    // ── Outstanding balance ─────────────────────────────────────────
    const outstanding = useMemo(() => {
        const unpaid = invoices.filter(isUnpaid);
        const total = unpaid.reduce((s, i) => s + (Number(i.amount ?? i.totalAmount) || 0), 0);
        const overdue = unpaid.filter(i => {
            const due = i.dueDate || i.billingDate || i.invoiceDate;
            return due && new Date(due) < now;
        });
        const earliestUnpaidDue = unpaid
            .map(i => i.dueDate || i.billingDate || i.invoiceDate)
            .filter(Boolean)
            .sort()[0];
        return { count: unpaid.length, total, overdueCount: overdue.length, nextDue: earliestUnpaidDue };
    }, [invoices, now]);

    // ── Next charge preview ─────────────────────────────────────────
    const nextCharge = useMemo(() => {
        // Shared resolver — must agree with what the billing run charges. This read
        // only the SINGULAR pricing.override and trusted override.status, so a
        // discount held in the pricing.overrides ARRAY was invisible here too.
        const { effectivePrice: resolvedPrice } = resolveEffectivePricing(customer, null, now);
        const effectivePrice = typeof resolvedPrice === 'number' ? resolvedPrice : (customer?.monthlyFee || 0);
        const pm = (paymentMethods || []).find(p => p.isDefault) || (paymentMethods || [])[0] || null;
        let pmLabel = 'No payment method on file';
        if (pm) {
            if (pm.type === 'card' || pm.type === 'credit_card') {
                pmLabel = `${(pm.brand || 'Card').toUpperCase()} •••• ${pm.last4 || '????'}`;
            } else if (pm.type === 'au_becs_debit' || pm.type === 'becs') {
                pmLabel = `BECS DD${pm.bsb ? ` · BSB ${pm.bsb}` : ''}${pm.last4 ? ` · ****${pm.last4}` : ''}`;
            } else {
                pmLabel = pm.type || 'Saved payment method';
            }
        }
        let daysUntil = null;
        if (customer?.nextBillingDate) {
            const diff = new Date(customer.nextBillingDate).getTime() - now.getTime();
            daysUntil = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        }
        return { amount: effectivePrice, date: customer?.nextBillingDate, daysUntil, pmLabel, hasPaymentMethod: !!pm };
    }, [customer, paymentMethods, now]);

    // ── Active promo ───────────────────────────────────────────────
    const promo = useMemo(() => {
        const resolved = resolveEffectivePricing(customer, null, now);
        if (!resolved.override) return null;
        return {
            effectivePrice: resolved.effectivePrice,
            basePrice: resolved.baseAmount ?? 0,
            discountPerMonth: resolved.discountPerMonth,
            endDate: resolved.endDate ? new Date(resolved.endDate) : null,
            reason: resolved.override.reason,
        };
    }, [customer, now]);

    // ── 12-month spending chart ─────────────────────────────────────
    const chart = useMemo(() => {
        const map = {};
        for (let i = 11; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            map[key] = 0;
        }
        invoices.filter(isPaid).forEach(i => {
            const ds = i.paymentDate || i.billingDate || i.invoiceDate;
            if (!ds) return;
            const d = new Date(ds);
            if (isNaN(d.getTime())) return;
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (map[key] !== undefined) map[key] += Number(i.amount ?? i.totalAmount) || 0;
        });
        const items = Object.entries(map).map(([k, v]) => ({ key: k, label: fmtMonthShort(k), amount: v }));
        const max = Math.max(0, ...items.map(i => i.amount));
        const totalSpend = items.reduce((s, i) => s + i.amount, 0);
        return { items, max, totalSpend };
    }, [invoices, now]);

    // Recent 5 invoices for quick reference
    const recentInvoices = useMemo(() => {
        return [...invoices]
            .sort((a, b) => new Date(b.billingDate || b.invoiceDate || 0) - new Date(a.billingDate || a.invoiceDate || 0))
            .slice(0, 5);
    }, [invoices]);

    const risk = RISK_STYLES[insights?.churnSignals?.riskLevel || 'low'];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#282828] rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-[#3c3836]">
                {/* Header */}
                <div className="p-6 border-b border-[#3c3836] flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-2xl font-bold text-[#ebdbb2]">Billing Summary</h2>
                        <p className="text-sm text-[#928374]">{customer?.fullName} · {customer?.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <CsvExportButton
                            headers={['Invoice', 'Date', 'Amount', 'Status', 'Payment Method', 'Type', 'Paid At']}
                            rows={invoices.map(h => [
                                h.invoiceNumber || '',
                                h.billingDate ? new Date(h.billingDate).toISOString().slice(0, 10) : '',
                                (Number(h.amount ?? h.totalAmount) || 0).toFixed(2),
                                h.status || '',
                                h.paymentMethod || '',
                                h.type || '',
                                h.paymentDate ? new Date(h.paymentDate).toISOString().slice(0, 10) : '',
                            ])}
                            filename={`peaknet-billing-summary-${customer?.accountNumber || customer?.email}-${new Date().toISOString().slice(0, 10)}.csv`}
                            label="Export CSV"
                            summary={[
                                `Customer: ${customer?.fullName} (${customer?.email})`,
                                `Account: ${customer?.accountNumber || ''}`,
                                `LTV: ${insights ? fmtAud(insights.lifetimeValue) : 'n/a'}`,
                                `Outstanding: ${fmtAud(outstanding.total)}`,
                                `Generated: ${new Date().toISOString()}`,
                            ]}
                        />
                        <button onClick={onClose} className="text-[#928374] hover:text-[#bdae93]">
                            <FaTimes size={24} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    {loading && (
                        <div className="flex items-center gap-2 text-[#928374] py-12 justify-center">
                            <FaSpinner className="animate-spin" /> Loading billing summary…
                        </div>
                    )}

                    {error && !loading && (
                        <div className="bg-[#3c1f1f] border border-[#cc241d] rounded p-3 text-sm text-[#fb4934]">
                            {error}
                        </div>
                    )}

                    {!loading && !error && (
                        <>
                            {/* Outstanding balance hero */}
                            <div className={`rounded-lg p-5 border ${outstanding.total > 0
                                ? 'bg-[#3c3320] border-[#d79921]'
                                : 'bg-[#3c3836] border-[#458588]'}`}>
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-[#928374] font-semibold flex items-center gap-2">
                                            {outstanding.total > 0 ? <FaReceipt /> : <FaCheckCircle />} Outstanding Balance
                                        </p>
                                        <p className="text-3xl font-bold text-[#ebdbb2] mt-1">{fmtAud(outstanding.total)}</p>
                                        <p className="text-sm text-[#bdae93] mt-1">
                                            {outstanding.total === 0
                                                ? 'Customer is fully paid up.'
                                                : outstanding.overdueCount > 0
                                                    ? `${outstanding.count} unpaid · ${outstanding.overdueCount} OVERDUE`
                                                    : `${outstanding.count} unpaid · earliest due ${fmtDate(outstanding.nextDue)}`}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Next charge + LTV + Avg spend row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-4">
                                    <p className="text-xs uppercase font-semibold text-[#928374] flex items-center gap-2 mb-2">
                                        <FaCalendarAlt /> Next Charge
                                    </p>
                                    <p className="text-2xl font-bold text-[#ebdbb2]">{fmtAud(nextCharge.amount)}</p>
                                    <p className="text-xs text-[#928374] mt-1">
                                        on {fmtDate(nextCharge.date)}
                                        {typeof nextCharge.daysUntil === 'number' ? ` (${nextCharge.daysUntil}d)` : ''}
                                    </p>
                                    <p className={`text-xs mt-2 flex items-center gap-1 ${nextCharge.hasPaymentMethod ? 'text-[#928374]' : 'text-[#fabd2f]'}`}>
                                        <FaCreditCard size={11} /> {nextCharge.pmLabel}
                                    </p>
                                </div>
                                <div className="bg-[#3c3836] border border-[#458588] rounded-lg p-4">
                                    <p className="text-xs uppercase font-semibold text-[#83a598] mb-2">Lifetime Value</p>
                                    <p className="text-2xl font-bold text-[#b8bb26]">
                                        {insights ? fmtAud(insights.lifetimeValue) : '—'}
                                    </p>
                                    <p className="text-xs text-[#928374] mt-1">
                                        {insights ? `${insights.paidInvoiceCount} paid · ${fmtAud(insights.lifetimeNetExGst)} ex-GST` : ''}
                                    </p>
                                </div>
                                <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-4">
                                    <p className="text-xs uppercase font-semibold text-[#928374] mb-2">Avg Monthly Spend</p>
                                    <p className="text-2xl font-bold text-[#ebdbb2]">
                                        {insights ? fmtAud(insights.averageMonthlySpend) : '—'}
                                    </p>
                                    <p className="text-xs text-[#928374] mt-1">
                                        {insights ? `${(insights.monthsActive || 0).toFixed(1)} months active` : ''}
                                    </p>
                                </div>
                            </div>

                            {/* Active promo */}
                            {promo && (
                                <div className="bg-[#3c3320] border border-[#d79921] rounded-lg p-3 flex items-start gap-2">
                                    <FaGift className="text-[#fabd2f] mt-0.5 flex-shrink-0" />
                                    <div className="text-sm text-[#fbf1c7]">
                                        <p className="font-medium text-[#fabd2f]">
                                            Active discount: {fmtAud(promo.discountPerMonth)}/month off
                                        </p>
                                        <p className="text-xs mt-0.5">
                                            Paying {fmtAud(promo.effectivePrice)}/mo vs base {fmtAud(promo.basePrice)}
                                            {promo.endDate ? ` · ends ${fmtDate(promo.endDate)}` : ' · ongoing'}
                                            {promo.reason && promo.reason !== 'promotion' ? ` · ${promo.reason}` : ''}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Churn risk */}
                            {insights && (
                                <div className={`${risk.bg} ${risk.border} border rounded-lg p-3`}>
                                    <div className="flex items-start gap-2">
                                        <FaUserClock className={risk.text} />
                                        <div className="flex-1">
                                            <p className={`text-xs font-bold uppercase ${risk.text}`}>
                                                Churn Risk: {risk.label}
                                            </p>
                                            {insights.churnSignals?.reasons?.length > 0 ? (
                                                <ul className="text-xs text-[#bdae93] mt-1 list-disc list-inside space-y-0.5">
                                                    {insights.churnSignals.reasons.map(r => <li key={r}>{r}</li>)}
                                                </ul>
                                            ) : (
                                                <p className="text-xs text-[#928374] mt-1">No red flags. Healthy account.</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 12-month spending chart */}
                            <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs uppercase font-semibold text-[#928374] flex items-center gap-2">
                                        <FaChartBar /> Spending — last 12 months
                                    </p>
                                    <p className="text-lg font-bold text-[#ebdbb2]">{fmtAud(chart.totalSpend)}</p>
                                </div>
                                {chart.max === 0 ? (
                                    <p className="text-sm text-[#928374] text-center py-4">No paid invoices in the last 12 months.</p>
                                ) : (
                                    // items-stretch + h-full on columns so the bar
                                    // track's flex-1 has vertical space to grow into.
                                    // items-end collapses columns to content height
                                    // and bars render at 0.
                                    <div className="flex items-stretch gap-2 h-36">
                                        {chart.items.map(it => {
                                            const h = chart.max > 0 ? Math.max(2, (it.amount / chart.max) * 100) : 2;
                                            const label = fmtCompact(it.amount);
                                            return (
                                                <div key={it.key} className="flex-1 flex flex-col items-center gap-1 h-full">
                                                    <span className="text-[10px] text-[#ebdbb2] font-semibold h-4 leading-4">
                                                        {label || ''}
                                                    </span>
                                                    <div className="w-full flex-1 flex items-end" title={`${it.label}: ${fmtAud(it.amount)}`}>
                                                        <div
                                                            className={`w-full rounded-t ${it.amount > 0 ? 'bg-gradient-to-t from-[#458588] to-[#83a598]' : 'bg-[#504945]'}`}
                                                            style={{ height: `${h}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-[10px] text-[#928374]">{it.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* 3-month forecast */}
                            {insights?.forecast3Month?.length > 0 && (
                                <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-4">
                                    <p className="text-xs uppercase font-semibold text-[#bdae93] flex items-center gap-2 mb-3">
                                        <FaCalendarCheck className="text-[#fabd2f]" /> Forecast — next 3 months
                                    </p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {insights.forecast3Month.map(f => (
                                            <div key={f.month} className="bg-[#1d2021] rounded p-3 text-center">
                                                <p className="text-[10px] text-[#928374]">{fmtMonth(f.month)}</p>
                                                <p className={`text-base font-bold ${f.promoApplied ? 'text-[#d3869b]' : 'text-[#ebdbb2]'}`}>
                                                    {fmtAud(f.expectedAmount)}
                                                </p>
                                                {f.promoApplied && (
                                                    <p className="text-[9px] text-[#d3869b] mt-0.5 inline-flex items-center gap-0.5">
                                                        <FaGift size={7} /> promo
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Recent invoices */}
                            <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-4">
                                <p className="text-xs uppercase font-semibold text-[#bdae93] mb-3">Recent Invoices (last 5)</p>
                                {recentInvoices.length === 0 ? (
                                    <p className="text-sm text-[#928374]">No invoices on record.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-[10px] uppercase text-[#928374] border-b border-[#504945]">
                                                <th className="py-1.5">Invoice</th>
                                                <th className="py-1.5">Date</th>
                                                <th className="py-1.5 text-right">Amount</th>
                                                <th className="py-1.5">Status</th>
                                                <th className="py-1.5">Method</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentInvoices.map(inv => (
                                                <tr key={inv.id || inv.invoiceNumber} className="border-b border-[#504945] last:border-0">
                                                    <td className="py-2 font-mono text-[#ebdbb2] text-xs">{inv.invoiceNumber}</td>
                                                    <td className="py-2 text-[#bdae93] text-xs">{fmtDate(inv.billingDate || inv.invoiceDate)}</td>
                                                    <td className="py-2 text-right text-[#ebdbb2]">{fmtAud(inv.amount ?? inv.totalAmount)}</td>
                                                    <td className="py-2 text-xs">
                                                        <span className={
                                                            isPaid(inv) ? 'text-[#b8bb26]' :
                                                            isFailed(inv) ? 'text-[#fb4934]' :
                                                            'text-[#fabd2f]'
                                                        }>{inv.status}</span>
                                                    </td>
                                                    <td className="py-2 text-xs text-[#928374]">{inv.paymentMethod || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {/* Failed payment timeline */}
                            {insights?.paymentFailures?.length > 0 && (
                                <div className="bg-[#3c1f1f] border border-[#cc241d] rounded-lg p-4">
                                    <p className="text-xs uppercase font-semibold text-[#fb4934] flex items-center gap-2 mb-3">
                                        <FaTimesCircle /> Payment Failures ({insights.failedInvoiceCount} total · {insights.recentFailureCount} in last 90d)
                                    </p>
                                    <div className="space-y-1 max-h-44 overflow-y-auto">
                                        {insights.paymentFailures.map((f, idx) => (
                                            <div key={`${f.invoiceNumber}-${idx}`} className="flex items-start justify-between gap-3 text-xs py-1 border-b border-[#3c3836] last:border-0">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[#ebdbb2] font-mono">{f.invoiceNumber}</p>
                                                    <p className="text-[10px] text-[#928374]">
                                                        {fmtDate(f.date)}{f.error ? ` — ${f.error}` : ''}
                                                    </p>
                                                </div>
                                                <p className="text-[#fb4934] font-medium whitespace-nowrap">{fmtAud(f.amount)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminBillingSummary;
